import React from 'react';
import {
  ChipField,
  Loading,
  RecordContextProvider,
  SingleFieldList,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { Grid } from '@mui/material';

import auditTrackDiffGenerator, {
  AuditTrack,
  ExtendedAuditTrackDiff,
} from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import ChipStringField from '../../customFields/ChipStringField';
import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';
import ArrayDiffField from '../utils/auditTracks/ArrayDiffField';
import DateDiffField from '../utils/auditTracks/DateDiffField';
import SelectDiffField from '../utils/auditTracks/SelectDiffField';
import StatusDiffField from '../utils/auditTracks/StatusDiffField';
import ReferenceArrayDiffField from '../utils/auditTracks/ReferenceArrayDiffField';
import ReferenceEntityField from '../../customFields/ReferenceEntityField';
import EntityDisplayNameField from '../../customFields/EntityDisplayNameField';
import countries from '../../constants/countries';
import { bankAccountTypesChoices } from '../../constants/bankAccountTypes';
import { bankAccountStatusesChoices } from '../../constants/bankAccountStatuses';
import resourceNames from '../../constants/resourceNames';
import { IAccountEntity } from '../../utilities/schemas/accountEntity';

const AccountEntityAuditDetails = () => {
  const record = useRecordContext<AuditTrack<IAccountEntity>>();
  const { data, isLoading } = useGetOne<AuditTrack<IAccountEntity>>(
    `audit-tracks/${record?.id}/previous`,
    { id: '' },
    { enabled: !!record },
  );

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator({
    current: record,
    previous: !isElementCreated ? data : undefined,
  }) as ExtendedAuditTrackDiff<IAccountEntity>;

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    id: resourceData.id?.hasChanged,
    customIds: resourceData.customIds?.hasChanged,
    bankgiroNumber: resourceData.bankgiroNumber?.hasChanged,
    plusgiroNumber: resourceData.plusgiroNumber?.hasChanged,
    createdAt: resourceData.createdAt?.hasChanged,
    provider: resourceData.provider?.hasChanged,
    nonBankingInstitution: resourceData.nonBankingInstitution?.hasChanged,
    closed: resourceData.closed?.hasChanged,
    opened: resourceData.opened?.hasChanged,
    source: resourceData.source?.hasChanged,
    iban: resourceData.iban?.hasChanged,
    bban: resourceData.bban?.hasChanged,
    bic: resourceData.bic?.hasChanged,
    country: resourceData.country?.hasChanged,
    product: resourceData.product?.hasChanged,
    accountName: resourceData.accountName?.hasChanged,
    accountType: resourceData.accountType?.hasChanged,
    status: resourceData.status?.hasChanged,
    bankAccountStatus: resourceData.bankAccountStatus?.hasChanged,
    connectedEntities: resourceData.connectedEntities?.hasChanged,
    riskScore: resourceData.riskScore?.hasChanged,
    scoreLatestCalculationDate: resourceData.scoreLatestCalculationDate?.hasChanged,
    kycApprovedAt: resourceData.kycApprovedAt?.hasChanged,
    currencies: resourceData.currencies?.hasChanged,
    groups: resourceData.groups?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.id && <TextDiffField source="id" label="ID" />}

          {hasFieldChanges.customIds && (
            <ArrayDiffField source="customIds" label="Custom IDs">
              <SingleFieldList linkType={false}>
                <ChipStringField />
              </SingleFieldList>
            </ArrayDiffField>
          )}

          {hasFieldChanges.bankgiroNumber && (
            <TextDiffField source="bankgiroNumber" label="Bankgiro number" />
          )}

          {hasFieldChanges.plusgiroNumber && (
            <TextDiffField source="plusgiroNumber" label="Plusgiro number" />
          )}

          {hasFieldChanges.createdAt && <DateDiffField source="createdAt" label="Created at" showTime />}

          {hasFieldChanges.provider && <TextDiffField source="provider" label="Provider" />}

          {hasFieldChanges.nonBankingInstitution && (
            <TextDiffField source="nonBankingInstitution" label="Non-banking institution" />
          )}

          {hasFieldChanges.closed && <DateDiffField source="closed" label="Closed" />}

          {hasFieldChanges.opened && <DateDiffField source="opened" label="Opened" />}

          {hasFieldChanges.source && <TextDiffField source="source" label="Source" />}

          {hasFieldChanges.iban && <TextDiffField source="iban" label="IBAN" />}

          {hasFieldChanges.bban && <TextDiffField source="bban" label="BBAN" />}

          {hasFieldChanges.bic && <TextDiffField source="bic" label="BIC" />}

          {hasFieldChanges.country && (
            <SelectDiffField source="country" label="Country" choices={countries} />
          )}

          {hasFieldChanges.product && <TextDiffField source="product" label="Product" />}

          {hasFieldChanges.accountName && (
            <TextDiffField source="accountName" label="Account name" />
          )}

          {hasFieldChanges.accountType && (
            <SelectDiffField
              source="accountType"
              label="Account type"
              choices={bankAccountTypesChoices}
            />
          )}

          {hasFieldChanges.status && <StatusDiffField source="status" label="Status" />}

          {hasFieldChanges.bankAccountStatus && (
            <SelectDiffField
              source="bankAccountStatus"
              label="Bank account status"
              choices={bankAccountStatusesChoices}
            />
          )}

          {hasFieldChanges.connectedEntities && (
            <ArrayDiffField source="connectedEntities" label="Connected entities">
              <SingleFieldList linkType={false}>
                <ReferenceEntityField source="">
                  <EntityDisplayNameField chip />
                </ReferenceEntityField>
              </SingleFieldList>
            </ArrayDiffField>
          )}

          {hasFieldChanges.riskScore && <TextDiffField source="riskScore" label="Risk score" />}

          {hasFieldChanges.scoreLatestCalculationDate && (
            <DateDiffField source="scoreLatestCalculationDate" label="Score latest calculation date" />
          )}

          {hasFieldChanges.kycApprovedAt && (
            <DateDiffField source="kycApprovedAt" label="KYC approved at" />
          )}

          {hasFieldChanges.currencies && (
            <ArrayDiffField source="currencies" label="Currencies">
              <SingleFieldList linkType={false}>
                <ChipStringField />
              </SingleFieldList>
            </ArrayDiffField>
          )}

          {hasFieldChanges.groups && (
            <ReferenceArrayDiffField
              source="groups"
              label="Groups"
              reference={resourceNames.GROUPS}
            >
              <SingleFieldList linkType={false}>
                <ChipField source="name" size="small" />
              </SingleFieldList>
            </ReferenceArrayDiffField>
          )}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default AccountEntityAuditDetails;
